import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SurferFilter.module.css';

const getQueryParamName = queryParamNames =>
  Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;

const parseRangeString = value => {
  if (!value || typeof value !== 'string') return null;

  const parts = value.split(',');
  if (parts.length !== 2) return null;

  const start = Number(parts[0]);
  const end = Number(parts[1]);

  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return { start, end };
};

/**
 * Format: minimum value to infinity
 * selected min => "min,500"
 * all/null => remove filter
 */
const format = (selectedMin, queryParamName, max) => {
  if (selectedMin == null || selectedMin === 0) {
    return { [queryParamName]: null };
  }

  const v = Number(selectedMin);
  if (!Number.isFinite(v)) return { [queryParamName]: null };

  // Use a very large number to represent "no upper limit"
  const largeMax = 999999;
  return { [queryParamName]: `${v},${largeMax}` };
};

const SurfaceInputField = props => {
  const { input, min = 0, max = 500, placeholder = "0", unit = "m²" } = props;
  const { value, onChange } = input;

  const handleChange = e => {
    const val = e.target.value;

    if (val === '') {
      onChange(null);
      return;
    }

    const num = Number(val);

    if (Number.isFinite(num) && num >= min && num <= max) {
      onChange(num);
    }
  };

  const handleBlur = e => {
    const val = e.target.value;

    if (val === '' || val === '0') {
      onChange(null);
      return;
    }

    const num = Number(val);
    if (Number.isFinite(num)) {
      onChange(Math.min(Math.max(num, min), max));
    }
  };

  return (
    <div className={css.container}>
      <label className={css.label}>Min</label>
      <div className={css.inputWrapper}>
        <input
          type="number"
          className={css.input}
          value={value == null ? '' : value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          min={min}
          max={max}
          step="1"
        />
        <span className={css.unit}>{unit}</span>
      </div>
    </div>
  );
};


const SurfaceFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    getAriaLabel,
    queryParamNames,
    initialValues,
    onSubmit,
    min = 0,
    max = 500,
    showAsPopup,
    contentPlacementOffset = 0,
    unit = "m²",
    ...rest
  } = props;

    const uiMin = min / 100;
    const uiMax = max / 100;

  const classes = classNames(rootClassName || css.root, className);
  const queryParamName = getQueryParamName(queryParamNames);

  const rawParam = initialValues?.[queryParamName];
  const parsed = rawParam ? parseRangeString(rawParam) : null;

  let selectedFromInitial = null;
  if (parsed) {
    const { start } = parsed;
    // If we have a start value, use it
    if (start > 0) {
      selectedFromInitial = start / 100;
    }
  }

  const isSelected = selectedFromInitial != null;
  const namedInitialValues = { [name]: selectedFromInitial ?? null };

    const toCenti = v => {
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? Math.ceil(n * 100) : null;
    };

    const handleSubmit = values => {
    const v = values ? values[name] : null;
    const centi = v == null ? null : toCenti(v);
    onSubmit(format(centi, queryParamName, max));
    };

  const aria = getAriaLabel ? getAriaLabel(label, rawParam || '') : label;

  const labelSelection = isSelected ? `${selectedFromInitial}${unit}+` : "";

  const fieldNode = (
    <Field
      name={name}
      render={({ input }) => (
        <SurfaceInputField
          input={input}

          min={uiMin}
          max={uiMax}
          unit={unit}
          placeholder="0"
        />
      )}
    />
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={label}
      labelSelection={labelSelection}
      ariaLabel={aria}
      isSelected={isSelected}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      {fieldNode}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      labelSelection={labelSelection}
      ariaLabel={aria}
      isSelected={isSelected}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      {fieldNode}
    </FilterPlain>
  );
};

export default SurfaceFilter;